// ---------------------------------------------------------------------------
// ยูทิลิตี้ภาษีไทย — ตรวจเลขผู้เสียภาษี / รหัสสาขา / คำนวณ VAT / จำนวนเงินตัวอักษร
// ---------------------------------------------------------------------------
// เงินคำนวณเป็น "สตางค์" (integer) ตลอด ปัดเศษครั้งเดียวระดับเอกสาร (half-up) แล้วค่อยแปลงกลับเป็นบาท
// ---------------------------------------------------------------------------

export const VAT_RATE = 7;

/** เลขผู้เสียภาษี/เลขบัตรประชาชน 13 หลัก — ตรวจ checksum (mod 11) */
export const validateThaiTaxId = (value) => {
  const id = String(value ?? "").trim();
  if (!/^\d{13}$/.test(id)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i += 1) sum += Number(id[i]) * (13 - i);
  return (11 - (sum % 11)) % 10 === Number(id[12]);
};

/** ตัวเลขล้วนจากข้อความ (ตัดขีด/ช่องว่าง) */
export const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

/**
 * รหัสสาขา 5 หลัก — ว่าง = สำนักงานใหญ่ "00000" · ไม่ใช่ตัวเลข/เกิน 5 หลัก คืน null
 */
export const normalizeBranch = (value) => {
  const raw = String(value ?? "").trim();
  if (raw === "") return "00000";
  if (!/^\d{1,5}$/.test(raw)) return null;
  return raw.padStart(5, "0");
};

export const branchLabel = (code) => (code === "00000" || !code ? "สำนักงานใหญ่" : `สาขา ${code}`);

const toSatang = (baht) => Math.round((Number(baht) || 0) * 100);
const divRound = (a, b) => Math.floor((a * 2 + b) / (b * 2)); // half-up สำหรับค่าไม่ติดลบ

/**
 * คำนวณ VAT ระดับเอกสาร
 *   lines    = [{ price, quantity }]  (บาท)
 *   discount = ส่วนลดทั้งใบ (บาท)
 *   mode     = "exclusive" (ราคายังไม่รวม VAT) | "inclusive" (ราคารวม VAT แล้ว)
 * คืนค่าเป็นบาท + ค่าสตางค์ (integer) ไว้เทียบ/เก็บ
 *   subtotal = รวมก่อนส่วนลด · base = มูลค่าก่อน VAT · vat · total = ยอดสุทธิ
 */
export const calcVat = ({ lines = [], discount = 0, mode = "exclusive", rate = VAT_RATE } = {}) => {
  const subtotalS = lines.reduce((sum, line) => sum + Math.round(toSatang(line.price) * (Number(line.quantity) || 0)), 0);
  const discountS = Math.min(Math.max(toSatang(discount), 0), Math.max(subtotalS, 0));
  const netS = Math.max(subtotalS, 0) - discountS;

  let baseS;
  let vatS;
  let totalS;
  if (mode === "inclusive") {
    totalS = netS;
    vatS = divRound(netS * rate, 100 + rate);
    baseS = netS - vatS;
  } else {
    baseS = netS;
    vatS = divRound(netS * rate, 100);
    totalS = netS + vatS;
  }

  return {
    mode,
    rate,
    subtotal: subtotalS / 100,
    discount: discountS / 100,
    base: baseS / 100,
    vat: vatS / 100,
    total: totalS / 100,
    subtotalSatang: subtotalS,
    discountSatang: discountS,
    baseSatang: baseS,
    vatSatang: vatS,
    totalSatang: totalS,
  };
};

// --------------------------------------------------------------------------
// จำนวนเงินเป็นตัวอักษรไทย
// --------------------------------------------------------------------------
const DIGIT = ["ศูนย์", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า"];
const PLACE = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน"];

// อ่านเลขไม่เกิน 6 หลัก (หนึ่งกลุ่มก่อนหลักล้าน) hasHigher = มีกลุ่มล้านนำหน้า (ใช้ตัดสิน "เอ็ด")
const readGroup = (digits, hasHigher) => {
  const len = digits.length;
  let out = "";
  let seenNonZero = hasHigher;
  for (let i = 0; i < len; i += 1) {
    const d = Number(digits[i]);
    const place = len - 1 - i;
    if (d === 0) continue;
    if (place === 1) out += d === 1 ? "สิบ" : d === 2 ? "ยี่สิบ" : `${DIGIT[d]}สิบ`;
    else if (place === 0) out += d === 1 && seenNonZero ? "เอ็ด" : DIGIT[d];
    else out += DIGIT[d] + PLACE[place];
    seenNonZero = true;
  }
  return out;
};

const readInteger = (str) => {
  const clean = str.replace(/^0+/, "");
  if (!clean) return "";
  if (clean.length <= 6) return readGroup(clean, false);
  const head = clean.slice(0, -6);
  const tail = clean.slice(-6);
  return `${readInteger(head)}ล้าน${readGroup(tail, true)}`;
};

/** 1234.5 -> "หนึ่งพันสองร้อยสามสิบสี่บาทห้าสิบสตางค์" */
export const bahtText = (amount) => {
  const satangTotal = Math.round(Math.abs(Number(amount) || 0) * 100);
  const bahtPart = Math.floor(satangTotal / 100);
  const satangPart = satangTotal % 100;
  const negative = Number(amount) < 0 && satangTotal > 0;

  const bahtWords = bahtPart === 0 ? DIGIT[0] : readInteger(String(bahtPart));
  const tail = satangPart === 0 ? "ถ้วน" : `${readInteger(String(satangPart))}สตางค์`;
  return `${negative ? "ลบ" : ""}${bahtWords}บาท${tail}`;
};
